import React from 'react';
import { useLanguage } from './context/LanguageContext';
import YouTube from 'react-youtube';
import { useMusic } from './context/MusicContext';
import { useMedia } from './context/MediaContext';

const MusicMiniPlayer = () => {
  const { t } = useLanguage();
  const playerRef = React.useRef(null);
  const { isMusicPlaying, setIsMusicPlaying, musicVolume } = useMusic();
  const { youtubePlaylistUrl } = useMedia();
  const [currentSongTitle, setCurrentSongTitle] = React.useState('Loading...');

  // Nur anzeigen wenn URL vorhanden ist UND Musik spielt
  if (!youtubePlaylistUrl || !isMusicPlaying) {
    return null;
  }

  return (
    <div className="fixed bottom-16 right-4 z-40 flex items-center justify-center">
      <div className="bg-white rounded-lg p-3 shadow-lg flex flex-col gap-2 w-[280px]">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-black">YouTube Music</div>
          <button
            onClick={() => {
              if (playerRef.current) {
                playerRef.current.stopVideo();
              }
              setIsMusicPlaying(false);
            }}
            className="text-gray-500 hover:text-gray-700"
            title={t('entertainment.stopMusic')}
          >
            ✕
          </button>
        </div>

        {/* Hidden YouTube Player */}
        <div className="hidden">
          <YouTube
            ref={playerRef}
            videoId={youtubePlaylistUrl.includes('list=') ? undefined : youtubePlaylistUrl.split('v=')[1]}
            opts={{
              width: '100%',
              height: '100',
              playerVars: {
                autoplay: 0,
                controls: 0,
                modestbranding: 1,
                playsinline: 1,
                rel: 0,
                showinfo: 0,
                listType: youtubePlaylistUrl.includes('list=') ? 'playlist' : undefined,
                list: youtubePlaylistUrl.includes('list=') ? new URLSearchParams(youtubePlaylistUrl.split('?')[1]).get('list') : undefined
              }
            }}
            onError={(e) => {
              setIsMusicPlaying(false);
            }}
            onReady={(event) => {
              playerRef.current = event.target;
              event.target.setVolume(musicVolume * 100);
              if (isMusicPlaying) {
                event.target.playVideo();
              }
              // Get initial video title
              try {
                const videoData = event.target.getVideoData();
                if (videoData && videoData.title) {
                  setCurrentSongTitle(videoData.title);
                }
              } catch (e) {
                console.error('Error getting video data:', e);
              }
            }}
            onPlay={() => {
              setIsMusicPlaying(true);
              // Update title when video plays
              if (playerRef.current) {
                try {
                  const videoData = playerRef.current.getVideoData();
                  if (videoData && videoData.title) {
                    setCurrentSongTitle(videoData.title);
                  }
                } catch (e) {
                  console.error('Error getting video data:', e);
                }
              }
            }}
            onPause={() => {
              setIsMusicPlaying(false);
            }}
            onEnd={() => {
              if (!youtubePlaylistUrl.includes('list=')) {
                setIsMusicPlaying(false);
              }
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (playerRef.current) {
                if (isMusicPlaying) {
                  playerRef.current.pauseVideo();
                } else {
                  playerRef.current.playVideo();
                }
              }
            }}
            className="w-8 h-8 flex items-center justify-center text-white rounded-full bg-amber-500 hover:bg-amber-600 transition-colors flex-shrink-0"
            title={isMusicPlaying ? 'Pause' : 'Play'}
          >
            {isMusicPlaying ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <div className="flex-1 overflow-hidden">
            <div className="text-xs font-medium text-black truncate" title={currentSongTitle}>
              {currentSongTitle}
            </div>
            <div className="text-xs text-gray-500">{t('entertainment.nowPlaying')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicMiniPlayer;
